import React from 'react';
import PropTypes from 'prop-types';
import getRostersForDay from '../../libs/rosterDataFormatter';
import Card from '../../components/Card/Card';
import {
  durationInRosterHourDisplayFormat,
  isOnSameDate,
  serializeYesplanAPIDate
} from '../../libs/dateTimeUtilities';
import {
  capture,
  PH_EVENT_DETAIL_CLICKED,
  PH_ROSTER_DAY_TOGGLED
} from '../../libs/posthogAnalytics';
import {
  printInHourDisplayFormat,
  printInShortFormatOrAsRelativeDate,
  printInShortDateAndHourFormat
} from '../../libs/dateTimePrinting';
import getUserDefaultTabHash from '../../libs/userUtilities';
import { setScrollPositionAppContainer } from '../../libs/fixDefaultScrollposition';
import Button from '../../components/Button/Button';
import { Body, Caption, Meta } from '../../styled components/Typography';
import { UIBar, UIBarGrowItem } from '../../styled components/UIBar';
import {
  BookingItem,
  BookingItemMetaText,
  BookingItemText,
  ChangeListDivider,
  ChangesButtonContainer,
  ChangesContainerAnimated,
  ChangesList,
  ChangesListItem,
  ChangesListMarginBottomContainer,
  SummarizedBooking,
  SummarizedBookingBookingsContainer,
  SummarizedBookingDivider,
  SummarizedBookingStartHour,
  SummarizedBookingTimes,
  TopLevelItem,
  TopLevelItemBody,
  TopLevelItemHour,
  TopLevelItemLabel,
  RosterItemDateDividerContainer,
  RosterItemDateDividerText
} from './RosterWeekViewDay.style';
import Checkbox from '../../components/Checkbox/Checkbox';
import FormatSettingsContext from '../../components/FormatSettingsContext';
import NavigationHistory from '../../libs/NavigationHistory';

/* Animations */
const DrawerTransitionVariants = {
  closed: {
    opacity: 0,
    height: 0
  },
  open: {
    opacity: 1,
    height: 'auto'
  }
};
/* End Animations */

class RosterWeekViewDay extends React.Component {
  static getDerivedStateFromProps(props, state) {
    const newNotifications = props.notifications;

    let showChanges;
    if (state.manuallyChangedShowChanges) {
      showChanges = state.showChanges;
    } else {
      showChanges = newNotifications.shouldShowWidgetChangeNotificationsOnDate(
        props.dayDateObject,
        state.showChanges
      );
    }
    const { dayDateObject, schedules, dayline } = props;

    const { dateFormatSetting, timeFormatSetting } = props.contextAsProp;

    const dayRoster = getRostersForDay(
      dayDateObject,
      schedules,
      dayline,
      dateFormatSetting,
      timeFormatSetting
    );

    return {
      ...state,
      dayRoster,
      showChanges
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      dayRoster: getRostersForDay(props.dayDateObject, props.schedules, props.dayline),
      manuallyChangedShowChanges: false,
      showChanges: props.notifications.hasUnreadNotificationsOnDate(props.dayDateObject),
      showAllChanges: false
    };
    this.htmlElement = React.createRef();

    this.handleHistoryButtonClick = this.handleHistoryButtonClick.bind(this);
    this.toggleShowAllChanges = this.toggleShowAllChanges.bind(this);
    this.handleEventClick = this.handleEventClick.bind(this);
    this.handleToggleCardCallback = this.handleToggleCardCallback.bind(this);
  }

  componentDidMount() {
    const { currentRosterDayDateObject, dayDateObject } = this.props;
    /* When opening the roster for a specific day, scroll to it */
    if (
      currentRosterDayDateObject &&
      isOnSameDate(dayDateObject, currentRosterDayDateObject)
    ) {
      const self = this;
      this.scrollToDayTimer = setTimeout(() => {
        if (self.cardElement && self.cardElement.htmlElement) {
          const offset = self.cardElement.htmlElement.getBoundingClientRect();
          const headerEls = document.querySelectorAll('[data-type="PageHeader"]');
          if (headerEls.length) {
            const visiblePageHeader = Array.from(headerEls).find(
              (headerEl) => headerEl.offsetWidth > 0 || headerEl.offsetHeight > 0
            );
            if (visiblePageHeader && offset && offset.top) {
              const headerHeight = parseFloat(
                getComputedStyle(visiblePageHeader, null).height.replace('px', '')
              );
              setScrollPositionAppContainer(offset.top - headerHeight - 5, null, true);
            }
          }
        }
      }, 300);
    }
  }

  componentWillUnmount() {
    /* The scroll-into-view timeout outlives the component otherwise, which keeps a timer
       pending after unmount — it reaches for the DOM node that is no longer there, and leaves
       Jest workers with an active timer at teardown. */
    clearTimeout(this.scrollToDayTimer);
  }

  handleHistoryButtonClick() {
    this.setState({
      manuallyChangedShowChanges: true,
      showChanges: !this.state.showChanges
    });
  }

  handleEventClick(linkToEvent, eventName) {
    const { user } = this.props;
    setScrollPositionAppContainer(0);
    this.props.navigationHistory.goTo({
      pathname: linkToEvent,
      hash: getUserDefaultTabHash(user),
      state: { headerTitle: eventName }
    });
  }

  handleToggleCardCallback(isVisible) {
    const { dateFormatSetting } = this.context;
    const date = serializeYesplanAPIDate(this.props.dayDateObject);
    const label = printInShortFormatOrAsRelativeDate(
      this.props.dayDateObject,
      dateFormatSetting,
      true,
      false
    );
    capture(PH_ROSTER_DAY_TOGGLED, {
      label,
      date,
      isVisible
    });
  }

  getBookingDetails(rosterBooking) {
    return this.props.bookings.find((booking) => booking.id === rosterBooking.booking.id);
  }

  getEventDetails(rosterBooking) {
    let event = false;
    const booking = this.getBookingDetails(rosterBooking);
    if (booking) event = this.props.events.find((event) => event.id === booking.event.id);
    /* Take into account ad-hoc bookings */
    return event || (booking ? booking.event : false);
  }

  getLocationNamesForEvent(event) {
    return event.locations && event.locations.length > 0
      ? event.locations.map((loc) => loc.name)
      : [i18n('No location')];
  }

  isVisibleOnOpening(dayRosters) {
    const { currentRosterDayDateObject, dayDateObject } = this.props;
    return (
      currentRosterDayDateObject &&
      dayRosters.hasRosters() &&
      isOnSameDate(dayDateObject, currentRosterDayDateObject)
    );
  }

  rosterHourString(rosterTime, dayBoundary, isStartTime) {
    const onDate = isStartTime
      ? rosterTime.isSameOrAfter(dayBoundary)
      : rosterTime.isSameOrBefore(dayBoundary);

    return onDate
      ? printInHourDisplayFormat(rosterTime, this.context.timeFormatSetting)
      : String.fromCharCode(8943);
  }

  isUnavailabilityDay(dayRosters) {
    const startOfWork = dayRosters.getStartOfWork();
    const endOfWork = dayRosters.getEndOfWork();
    return startOfWork === null && endOfWork === null;
  }

  dayAnnotation(dayRosters, allHaveActuals) {
    if (dayRosters.hasRosters()) {
      const startOfWorkDateAndTimeObject = dayRosters.getStartOfWork();
      const endOfWorkDateAndTimeObject = dayRosters.getEndOfWork();
      // If the day start and end are null, this means there is only an unavailability
      // Instead of showing times, we hence show the reason of the unavailability (or Unavailable if no reason)
      if (this.isUnavailabilityDay(dayRosters)) {
        return dayRosters.getRosters()[0].reason || i18n('Unavailable');
      }
      if (allHaveActuals) {
        return durationInRosterHourDisplayFormat(this.props.counters.day.actual);
      }
      return `${this.rosterHourString(
        startOfWorkDateAndTimeObject,
        dayRosters.dayStart,
        true
      )} – ${this.rosterHourString(
        endOfWorkDateAndTimeObject,
        dayRosters.dayEnd,
        false
      )}`;
    }
    return '';
  }

  dayAnnotationColoring(dayRosters, allHaveActuals) {
    if (this.isUnavailabilityDay(dayRosters)) return 'red';
    if (allHaveActuals) return 'cyan';
    return 'default';
  }

  toggleShowAllChanges() {
    this.setState({
      showAllChanges: !this.state.showAllChanges
    });
  }

  renderTopLevelItem(
    item,
    typeName,
    variant,
    dayStartDateAndTimeObject,
    dayEndDateAndTimeObject,
    withEndTime,
    bodyFunction
  ) {
    const startHourString = this.rosterHourString(
      item.start,
      dayStartDateAndTimeObject,
      true
    );
    const endHourString = withEndTime
      ? this.rosterHourString(item.end, dayEndDateAndTimeObject, false)
      : null;
    return (
      <TopLevelItem
        key={item.type + startHourString}
        $variant={variant}
        data-test-type="rosterDay"
        data-test-variant={variant}
      >
        {typeName !== null && (
          <TopLevelItemHour data-test-type="timeslot" $variant={variant}>
            {`${startHourString} `}
            <TopLevelItemLabel $variant={variant}>{typeName}</TopLevelItemLabel>
          </TopLevelItemHour>
        )}
        {bodyFunction && (
          <TopLevelItemBody $variant={variant} $typename={typeName}>
            {bodyFunction()}
          </TopLevelItemBody>
        )}
        {endHourString && (
          <TopLevelItemHour data-test-type="timeslot" $variant={variant}>
            {endHourString}
          </TopLevelItemHour>
        )}
      </TopLevelItem>
    );
  }

  renderItemWithSummarizedBookings(
    item,
    typeName,
    variant,
    dayStartDateAndTimeObject,
    dayEndDateAndTimeObject,
    withEndTime
  ) {
    const hasBookings = item.summarizedBookings.length > 0;
    const bodyFunction = hasBookings
      ? () =>
          item.summarizedBookings.map((summarizedBooking) =>
            this.renderSummarizedBooking(
              summarizedBooking,
              dayStartDateAndTimeObject,
              dayEndDateAndTimeObject,
              false
            )
          )
      : null;

    return this.renderTopLevelItem(
      item,
      typeName,
      variant,
      dayStartDateAndTimeObject,
      dayEndDateAndTimeObject,
      withEndTime,
      bodyFunction
    );
  }

  renderShift(shift, dayStartDateAndTimeObject, dayEndDateAndTimeObject, withEndTime) {
    return this.renderItemWithSummarizedBookings(
      shift,
      i18n('On Duty'),
      shift.hasActuals ? 'actual-hour' : 'shift',
      dayStartDateAndTimeObject,
      dayEndDateAndTimeObject,
      withEndTime
    );
  }

  renderNoRoster(
    noRosterItem,
    dayStartDateAndTimeObject,
    dayEndDateAndTimeObject,
    withEndTime
  ) {
    return this.renderItemWithSummarizedBookings(
      noRosterItem,
      i18n('Time Off'),
      'no-roster',
      dayStartDateAndTimeObject,
      dayEndDateAndTimeObject,
      withEndTime
    );
  }

  renderBreak(
    breakItem,
    dayStartDateAndTimeObject,
    dayEndDateAndTimeObject,
    withEndTime
  ) {
    return this.renderItemWithSummarizedBookings(
      breakItem,
      i18n('Break'),
      breakItem.hasActuals ? 'actual-hour-break' : 'break',
      dayStartDateAndTimeObject,
      dayEndDateAndTimeObject,
      withEndTime
    );
  }

  renderUnavailability(
    unavailabilityItem,
    dayStartDateAndTimeObject,
    dayEndDateAndTimeObject,
    isLast,
    previousRosterItem
  ) {
    const overlapsWithItemBefore =
      previousRosterItem && unavailabilityItem.overlapsWith(previousRosterItem);
    // No unavailability if there are actuals
    let variant;
    if (unavailabilityItem.hasActuals) variant = 'actual-hour';
    else variant = overlapsWithItemBefore ? 'overlap' : 'unavailability';
    const reason = unavailabilityItem.reason || i18n('Unavailable');
    return this.renderItemWithSummarizedBookings(
      unavailabilityItem,
      reason,
      variant,
      dayStartDateAndTimeObject,
      dayEndDateAndTimeObject,
      isLast
    );
  }

  renderBooking(bookingRosterItem) {
    const { linkUrlFor, user } = this.props;
    const { booking } = bookingRosterItem;
    const eventDetails = this.getEventDetails(bookingRosterItem);
    const bookingDetails = this.getBookingDetails(bookingRosterItem);
    const eventName = eventDetails.name;
    const locations = eventDetails.locations
      ? this.getLocationNamesForEvent(eventDetails).join(', ')
      : null;
    let role = null;
    let comment = null;
    if (bookingDetails) {
      role =
        bookingDetails.role && bookingDetails.role.length > 0
          ? bookingDetails.role
          : null;
      comment =
        bookingDetails.comment && bookingDetails.comment.length > 0
          ? bookingDetails.comment
          : null;
    }
    const isUndisclosedBooking = !eventDetails;
    const onClickProperty = {};
    if (locations) {
      onClickProperty.onClick = () =>
        this.handleEventClick(linkUrlFor(`/event/${eventDetails.id}`), eventName);
      capture(PH_EVENT_DETAIL_CLICKED, {
        eventId: eventDetails.id,
        eventTitle: eventName,
        openedOnTab: getUserDefaultTabHash(user).slice(1),
        clickedInContextOf: 'roster'
      });
    }
    return (
      <BookingItem
        $hasActuals={bookingRosterItem.hasActuals}
        data-test-type="bookingFrame-item"
        key={booking.id}
        {...onClickProperty}
      >
        <div>
          <BookingItemText
            $hasActuals={bookingRosterItem.hasActuals}
            data-test-type="bookingFrame-headline"
          >
            {locations || eventName}
            {role && `, ${role}`}
          </BookingItemText>
          <div tw="space-y-8">
            {!isUndisclosedBooking && locations && (
              <BookingItemMetaText
                $hasActuals={bookingRosterItem.hasActuals}
                data-test-type="bookingFrame-subhead"
              >
                {eventName}
              </BookingItemMetaText>
            )}
            {isUndisclosedBooking && i18n('Undisclosed Booking')}
            {comment && (
              <BookingItemMetaText
                $hasActuals={bookingRosterItem.hasActuals}
                data-test-type="bookingFrame-description"
              >
                {comment}
              </BookingItemMetaText>
            )}
          </div>
        </div>
        {locations && <Button contentType="icon" size="small" icon="navigate-next" />}
      </BookingItem>
    );
  }

  renderSummarizedBooking(
    summarizedBooking,
    dayStartDateAndTimeObject,
    dayEndDateAndTimeObject,
    isTopLevel
  ) {
    const summarizedBookingStart = this.rosterHourString(
      summarizedBooking.start,
      dayStartDateAndTimeObject,
      true
    );
    const summarizedBookingEnd = this.rosterHourString(
      summarizedBooking.end,
      dayEndDateAndTimeObject,
      false
    );
    const { bookings } = summarizedBooking;
    const body = (
      <SummarizedBooking
        key={`booking${summarizedBookingStart}`}
        $hasActuals={summarizedBooking.hasActuals}
        data-test-type="bookingFrame"
      >
        <SummarizedBookingTimes>
          <SummarizedBookingDivider $hasActuals={summarizedBooking.hasActuals} />
          <SummarizedBookingStartHour
            $hasActuals={summarizedBooking.hasActuals}
            data-test-type="bookingFrame-startHour"
          >
            {summarizedBookingStart}
          </SummarizedBookingStartHour>
          <BookingItemMetaText
            $hasActuals={summarizedBooking.hasActuals}
            data-test-type="bookingFrame-endHour"
          >
            {summarizedBookingEnd}
          </BookingItemMetaText>
        </SummarizedBookingTimes>
        <SummarizedBookingBookingsContainer data-test-list="bookingFrame-bookings">
          {bookings.map((booking) => this.renderBooking(booking))}
        </SummarizedBookingBookingsContainer>
      </SummarizedBooking>
    );
    return isTopLevel
      ? this.renderTopLevelItem(
          summarizedBooking,
          null,
          'outside-shift',
          dayStartDateAndTimeObject,
          dayEndDateAndTimeObject,
          false,
          () => body
        )
      : body;
  }

  renderDateDivider(dateObject) {
    const { dateFormatSetting } = this.context;
    return (
      <RosterItemDateDividerContainer data-test-date-divider key="date-divider">
        <RosterItemDateDividerText>
          {printInShortFormatOrAsRelativeDate(dateObject, dateFormatSetting, true, false)}
        </RosterItemDateDividerText>
      </RosterItemDateDividerContainer>
    );
  }

  renderScheduleItem(
    rosterItem,
    dayStartDateAndTimeObject,
    dayEndDateAndTimeObject,
    withEndTime,
    previousRosterItem
  ) {
    switch (rosterItem.getType()) {
      case 'shift':
        return this.renderShift(
          rosterItem,
          dayStartDateAndTimeObject,
          dayEndDateAndTimeObject,
          withEndTime
        );
      case 'noroster':
        return this.renderNoRoster(
          rosterItem,
          dayStartDateAndTimeObject,
          dayEndDateAndTimeObject,
          withEndTime
        );
      case 'summarizedbooking':
        return this.renderSummarizedBooking(
          rosterItem,
          dayStartDateAndTimeObject,
          dayEndDateAndTimeObject,
          true
        );
      case 'unavailability':
        return this.renderUnavailability(
          rosterItem,
          dayStartDateAndTimeObject,
          dayEndDateAndTimeObject,
          withEndTime,
          previousRosterItem
        );
      case 'break':
        return this.renderBreak(
          rosterItem,
          dayStartDateAndTimeObject,
          dayEndDateAndTimeObject,
          withEndTime
        );
      default:
        throw new Error(
          `RosterWeekViewDay: unknown type for roster item: ${rosterItem.getType()}`
        );
    }
  }

  renderScheduleForDay(rosters) {
    const individualRosters = rosters.getRosters();
    const { dayStart } = rosters;
    const { dayEnd } = rosters;
    const nextDayStart = dayStart.clone().add(1, 'd');
    const { timeFormatSetting } = this.context;
    return (
      <div ref={this.htmlElement}>
        {individualRosters.map((rosterItem, index) => {
          const previousRosterItem = index === 0 ? null : individualRosters[index - 1];
          const nextRosterItem =
            index === individualRosters.length - 1 ? null : individualRosters[index + 1];
          const withEndTime =
            nextRosterItem === null ||
            ((nextRosterItem.type === 'summarizedbooking' ||
              nextRosterItem.type === 'unavailability') &&
              !nextRosterItem.start.isSame(rosterItem.end));

          const showDateDivider =
            !previousRosterItem &&
            isOnSameDate(rosterItem.start, nextDayStart) &&
            rosterItem.start.isSameOrBefore(nextDayStart);

          return (
            <React.Fragment
              key={`rosterItem-${printInShortDateAndHourFormat(
                rosterItem.start,
                timeFormatSetting
              )}`}
            >
              {showDateDivider && this.renderDateDivider(rosterItem.start)}
              {this.renderScheduleItem(
                rosterItem,
                dayStart,
                dayEnd,
                withEndTime,
                previousRosterItem
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  renderChangeButton() {
    const { showChanges } = this.state;
    return (
      <ChangesButtonContainer>
        <Button
          contentType="textWithIcon"
          icon={showChanges ? 'expand-top-point' : 'expand-bottom-point'}
          size="small"
          onClick={this.handleHistoryButtonClick}
          tw="mb-4"
        >
          {showChanges ? i18n('Hide Changes') : i18n('Show Changes')}
        </Button>
      </ChangesButtonContainer>
    );
  }

  renderChangeItem(changeItem, showDivider) {
    const { toggleNotificationUnreadStatus } = this.props;
    return (
      <ChangesListItem
        key={changeItem.getNotificationIdWithMessageHash()}
        onClick={() => toggleNotificationUnreadStatus(changeItem, true)}
        data-test-type="rosterchanges-listitem"
      >
        <ChangesListMarginBottomContainer>
          <ChangesListMarginBottomContainer>
            <Meta $color="black">
              {changeItem.printDetailedDescriptionRosterNotification()}
            </Meta>
          </ChangesListMarginBottomContainer>
          <UIBar>
            <Caption $color="gray dark">
              {changeItem.printTimeSinceNotificationText()}
            </Caption>
            <UIBarGrowItem />
            {!changeItem.isRead && (
              <Checkbox
                variant="rounded"
                onChange={() => toggleNotificationUnreadStatus(changeItem, true)}
              />
            )}
          </UIBar>
        </ChangesListMarginBottomContainer>
        {showDivider && <ChangeListDivider />}
      </ChangesListItem>
    );
  }

  renderNoChangeItems() {
    const { showAllChanges } = this.state;
    const message = showAllChanges ? i18n('No changes') : i18n('No unread changes');
    return (
      <ChangesListItem $center>
        <Body $color="gray dark">{message}</Body>
      </ChangesListItem>
    );
  }

  renderChangeItems() {
    const { showChanges, showAllChanges } = this.state;
    const { notifications, dayDateObject, maxNrOfNotificationsToShow } = this.props;
    let listContent;
    if (
      !notifications.shouldShowWidgetChangeNotificationsOnDate(
        dayDateObject,
        showAllChanges
      )
    )
      listContent = this.renderNoChangeItems();
    else {
      const notificationsForDay = notifications.getWidgetNotificationsForDate(
        dayDateObject,
        showAllChanges,
        maxNrOfNotificationsToShow
      );
      listContent = notificationsForDay.map((item) =>
        this.renderChangeItem(
          item,
          item.shouldPrintDividerAfterNotification(notificationsForDay)
        )
      );
    }
    return (
      <ChangesContainerAnimated
        variants={DrawerTransitionVariants}
        initial={showChanges ? 'open' : 'closed'}
        animate={showChanges ? 'open' : 'closed'}
      >
        <ChangesList data-test-type="rosterchanges-list">{listContent}</ChangesList>
      </ChangesContainerAnimated>
    );
  }

  renderChangesBar() {
    const { showAllChanges } = this.state;
    const { toggleNotificationUnreadStatus, notifications, dayDateObject } = this.props;
    const hasUnreadItems = notifications.hasUnreadNotificationsOnDate(dayDateObject);
    return (
      <UIBar tw="pl-8 pr-8 flex-wrap">
        <Button
          onClick={this.toggleShowAllChanges}
          size="small"
          color="primary"
          tw="mb-4"
        >
          {showAllChanges ? i18n('Show Unread') : i18n('Show All')}
        </Button>
        <UIBarGrowItem />
        <Button
          onClick={(event) => {
            event.stopPropagation();
            notifications
              .getWidgetNotificationsForDate(dayDateObject)
              .forEach((notification) => {
                if (!notification.getIsRead())
                  toggleNotificationUnreadStatus(notification, true);
              });
          }}
          size="small"
          color="primary"
          disabled={!hasUnreadItems}
          tw="mb-4"
        >
          {i18n('Mark All As Read')}
        </Button>
      </UIBar>
    );
  }

  renderChanges() {
    const { showChanges } = this.state;
    return (
      <div tw="pb-8">
        {this.renderChangeButton()}
        <div>
          {showChanges && this.renderChangesBar()}
          {this.renderChangeItems()}
        </div>
      </div>
    );
  }

  render() {
    const {
      dayDateObject,
      navigationHistory,
      getLinkUrlForCurrentWeekAndDay,
      notifications,
      maxNrOfNotificationsToShow
    } = this.props;
    const { dateFormatSetting } = this.context;

    const { dayRoster } = this.state;
    const allHaveActuals = dayRoster.hasRosters() && dayRoster.allHaveActuals();
    const annotation = this.dayAnnotation(dayRoster, allHaveActuals);
    const hasNotifications = notifications.hasWidgetNotificationsForDate(
      dayDateObject,
      maxNrOfNotificationsToShow
    );
    const hasUnreadNotifications =
      notifications.hasUnreadNotificationsOnDate(dayDateObject);
    return (
      <Card
        key={serializeYesplanAPIDate(dayDateObject)}
        showToggle
        canToggle={dayRoster.hasRosters() || hasNotifications}
        visible={this.isVisibleOnOpening(dayRoster)}
        annotation={annotation}
        editFunction={
          dayRoster.hasRosters() && !allHaveActuals
            ? null /* <= not editable yet */
            : null
        }
        hasLock={false /* <= do not show actual lock yet */ && allHaveActuals}
        title={printInShortFormatOrAsRelativeDate(
          dayDateObject,
          dateFormatSetting,
          true,
          false
        )}
        cardKey={serializeYesplanAPIDate(dayDateObject)}
        ref={(ref) => {
          this.cardElement = ref;
        }}
        greyedOut={!dayRoster.hasRosters() && !hasNotifications}
        annotationColoring={this.dayAnnotationColoring(dayRoster, allHaveActuals)}
        showBullet={hasUnreadNotifications}
        navigationHistory={navigationHistory}
        urlToOpen={getLinkUrlForCurrentWeekAndDay(dayDateObject, false)}
        urlToClose={getLinkUrlForCurrentWeekAndDay(dayDateObject, true)}
        replaceURL
        toggleCardCallback={this.handleToggleCardCallback}
      >
        {dayRoster.hasRosters() && this.renderScheduleForDay(dayRoster)}
        {hasNotifications && this.renderChanges()}
      </Card>
    );
  }
}

RosterWeekViewDay.propTypes = {
  currentRosterDayDateObject: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({})])
    .isRequired,
  dayline: PropTypes.string.isRequired,
  dayDateObject: PropTypes.shape({ isSame: PropTypes.func.isRequired }).isRequired,
  counters: PropTypes.shape({ day: PropTypes.shape({ actual: PropTypes.string }) }),
  schedules: PropTypes.arrayOf(
    PropTypes.shape({
      scheduletype: PropTypes.string
    })
  ).isRequired,
  bookings: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  events: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  notifications: PropTypes.shape({
    hasUnreadNotificationsOnDate: PropTypes.func,
    getWidgetNotificationsForDate: PropTypes.func,
    shouldShowWidgetChangeNotificationsOnDate: PropTypes.func
  }).isRequired,
  maxNrOfNotificationsToShow: PropTypes.number.isRequired,
  toggleNotificationUnreadStatus: PropTypes.func.isRequired,
  getLinkUrlForCurrentWeekAndDay: PropTypes.func.isRequired,
  linkUrlFor: PropTypes.func.isRequired,
  navigationHistory: PropTypes.instanceOf(NavigationHistory).isRequired,
  user: PropTypes.shape({
    preferences: PropTypes.shape({
      defaultEventInspectorTabApp: PropTypes.shape({})
    })
  }).isRequired,
  contextAsProp: PropTypes.shape({
    dateFormatSetting: PropTypes.string,
    timeFormatSetting: PropTypes.string
  }).isRequired
};

RosterWeekViewDay.defaultProps = {
  counters: { day: { actual: '0' } }
};

RosterWeekViewDay.contextType = FormatSettingsContext;

export const RosterWeekViewDayWithContext = (Component) => (props) =>
  (
    <FormatSettingsContext.Consumer>
      {(value) => <Component {...props} contextAsProp={value} />}
    </FormatSettingsContext.Consumer>
  );

export default RosterWeekViewDayWithContext(RosterWeekViewDay);
